import { useState, type FormEvent } from 'react';
import { parties, requests, workplace } from '../../api/endpoints';
import type {
    Contact, ItemParty, Party, RequestContact, RequestParty, RequestSnapshot,
    RequestSummary, SituationItem,
} from '../../api/types';
import { Card, Column, DataTable, Empty, ErrorBox, Field, fmtDate, fmtMoney, KV, label, shortId } from '../../components/ui';
import { useAsync } from '../../hooks';
import type { CardData } from '../RequestCardPage';
// === WT4 finance (§12): блок LTV клиентского счёта ===
import { ClientLtvCard } from './ClientLtvCard';

function nameOf(map: Map<string, string>, id?: string | null): string {
  if (!id) return '—';
  return map.get(id) ?? shortId(id);
}

function fmtOverdue(seconds?: number | null): string {
  if (seconds === null || seconds === undefined) return '—';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  if (days > 0) return `${days} дн. ${hours} ч`;
  const minutes = Math.floor((seconds % 3600) / 60);
  return hours > 0 ? `${hours} ч ${minutes} мин` : `${minutes} мин`;
}

/** Таб из action_url вида '#/requests/<id>?tab=events'. */
function tabFromActionUrl(url?: string): string | null {
  if (!url) return null;
  const query = url.split('?')[1];
  return query ? new URLSearchParams(query).get('tab') : null;
}

/** Блок «Текущая ситуация» (§5): активные события + блокеры закрытия. */
function SituationCard({ items, onAction }: {
  items: SituationItem[] | undefined;
  onAction: (item: SituationItem) => void;
}) {
  const columns: Column<SituationItem>[] = [
    { key: 'kind_label', title: 'Что происходит', render: (r) => r.kind_label ?? r.kind ?? '—' },
    { key: 'waiting_for', title: 'Ждём', render: (r) => (r.paused ? 'На паузе' : (r.waiting_for ?? '—')) },
    { key: 'item_title', title: 'Позиция', render: (r) => r.item_title ?? '—' },
    { key: 'due_at', title: 'Срок', render: (r) => fmtDate(r.due_at) },
    {
      key: 'overdue_seconds', title: 'Просрочка',
      render: (r) => (r.overdue_seconds != null && r.overdue_seconds > 0
        ? <span className="badge bad">{fmtOverdue(r.overdue_seconds)}</span>
        : '—'),
    },
    {
      key: 'reason', title: 'Примечание',
      render: (r) => r.reason ?? '—',
    },
    {
      key: 'action', title: 'Действие',
      render: (r) => (r.action_url
        ? <a href={r.action_url}
             onClick={(e) => { e.preventDefault(); e.stopPropagation(); onAction(r); }}>
            Перейти
          </a>
        : '—'),
    },
  ];
  return (
    <Card title="Текущая ситуация">
      <DataTable columns={columns} rows={items ?? []}
                 empty="Активных задач и блокеров нет" />
    </Card>
  );
}

/** Финансовый блок по валютам (валюты не суммируются, null → «—»). */
function FinanceCard({ buckets }: { buckets: NonNullable<RequestSummary['finance']> }) {
  const columns: Column<NonNullable<RequestSummary['finance']>[number]>[] = [
    { key: 'currency', title: 'Валюта', render: (r) => r.currency ?? '—' },
    { key: 'agreed_sale', title: 'Согласованная продажа', render: (r) => fmtMoney(r.agreed_sale, r.currency) },
    { key: 'cost', title: 'Себестоимость', render: (r) => fmtMoney(r.cost, r.currency) },
    { key: 'pre_tax_difference', title: 'Разница до налогов', render: (r) => fmtMoney(r.pre_tax_difference, r.currency) },
    { key: 'invoiced', title: 'Выставлено', render: (r) => fmtMoney(r.invoiced, r.currency) },
    { key: 'paid_net', title: 'Оплачено (с учётом возвратов)', render: (r) => fmtMoney(r.paid_net, r.currency) },
    { key: 'outstanding', title: 'Долг', render: (r) => fmtMoney(r.outstanding, r.currency) },
  ];
  return (
    <Card title="Финансы по валютам">
      <DataTable columns={columns}
                 rows={buckets.map((b, i) => ({ id: b.currency ?? `cur-${i}`, ...b }))}
                 empty="Финансовых данных нет" />
    </Card>
  );
}

/** Индикатор «Риск нарушения срока» (§5): risk=null → «недостаточно данных». */
function ScheduleRiskCard({ risk }: { risk: NonNullable<RequestSummary['schedule_risk']> }) {
  return (
    <Card title="Риск нарушения срока">
      {risk.risk === true && (
        <div className="alert alert-error">
          <strong>Риск нарушения обещанного срока.</strong>
          <span>{risk.reason}</span>
        </div>
      )}
      {risk.risk === false && (
        <div className="alert alert-info">
          <span>{risk.reason ?? 'Ближайшие шаги укладываются в обещанный срок.'}</span>
        </div>
      )}
      {(risk.risk === null || risk.risk === undefined) && (
        <div className="alert alert-warn">
          <strong>Недостаточно данных для оценки риска.</strong>
          <span>{risk.reason ?? 'Нет данных о ближайших шагах или обещанном сроке.'}</span>
        </div>
      )}
      <div className="kv-grid">
        <KV k="Обещанный срок" v={fmtDate(risk.promised_due_at)} />
        <KV k="Ближайший шаг" v={fmtDate(risk.nearest_due_at)} />
        <KV k="Ожидаемое завершение шага" v={fmtDate(risk.expected_at)} />
        <KV k="Буфер" v={risk.buffer_hours != null ? `${risk.buffer_hours} ч` : '—'} />
      </div>
    </Card>
  );
}

/** Компактная квалификация по позициям (§5). */
function QualificationCard({ rows }: { rows: NonNullable<RequestSummary['qualification']> }) {
  const columns: Column<NonNullable<RequestSummary['qualification']>[number]>[] = [
    { key: 'title', title: 'Позиция', render: (r) => r.title },
    { key: 'document_type_label', title: 'Документ', render: (r) => r.document_type_label ?? label(r.document_type) },
    { key: 'country_of_entry', title: 'Страна ввоза', render: (r) => r.country_of_entry ?? '—' },
    {
      key: 'applicant', title: 'Заявитель',
      render: (r) => (r.applicant
        ? `${r.applicant.name}${r.applicant_country ? ` (${r.applicant_country})` : ''}`
        : '—'),
    },
    { key: 'manufacturer', title: 'Изготовитель', render: (r) => r.manufacturer?.name ?? '—' },
    {
      key: 'products', title: 'Продукция',
      render: (r) => (r.products && r.products.length > 0
        ? r.products.map((p) =>
            [p.name, p.model, p.article, p.tnved ? `ТН ВЭД ${p.tnved}` : null]
              .filter(Boolean).join(' · ')).join('; ')
        : '—'),
    },
  ];
  return (
    <Card title="Квалификация по позициям">
      <DataTable columns={columns}
                 rows={rows.map((r) => ({ id: r.item_id, ...r }))}
                 empty="Позиций нет" />
    </Card>
  );
}

export function OverviewTab({ data, onTabChange }: {
  data: CardData;
  onTabChange?: (tab: string) => void;
}) {
  const { request } = data;
  const summary = useAsync<RequestSummary>(() => workplace.requestSummary(request.id), [request.id]);
  const contacts = useAsync<RequestContact[]>(() => requests.contacts(request.id), [request.id]);
  const reqParties = useAsync<RequestParty[]>(() => requests.parties(request.id), [request.id]);
  const snapshots = useAsync<RequestSnapshot[]>(() => requests.snapshots(request.id), [request.id]);
  const allParties = useAsync<Party[]>(() => parties.list(), []);
  const allContacts = useAsync<Contact[]>(() => parties.contacts(), []);
  const itemParties = useAsync<ItemParty[]>(async () => {
    const lists = await Promise.all(data.items.map((it) => requests.itemParties(it.id).catch(() => [] as ItemParty[])));
    return lists.flat();
  }, [data.items.map((i) => i.id).join(',')]);

  const [partyId, setPartyId] = useState('');
  const [role, setRole] = useState('payer');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);

  const partyNames = new Map((allParties.data ?? []).map((p) => [p.id, p.name]));
  const contactNames = new Map((allContacts.data ?? []).map((c) => [c.id, c.name]));

  const addParty = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await requests.addParty(request.id, partyId, role);
      setPartyId('');
      reqParties.reload();
    } catch (x) { setErr(x); } finally { setBusy(false); }
  };

  const goAction = (item: SituationItem) => {
    const tab = tabFromActionUrl(item.action_url);
    if (tab && onTabChange) {
      onTabChange(tab);
    } else if (item.action_url) {
      window.location.hash = item.action_url;
    }
  };

  const header = summary.data?.header;

  const contactCols: Column<RequestContact>[] = [
    { key: 'contact', title: 'Контакт', render: (r) => nameOf(contactNames, r.contact) },
    { key: 'role', title: 'Роль', render: (r) => label(r.role) },
    { key: 'is_primary', title: 'Основной', render: (r) => (r.is_primary ? 'Да' : '—') },
  ];

  const partyCols: Column<RequestParty>[] = [
    { key: 'party', title: 'Участник', render: (r) => nameOf(partyNames, r.party) },
    { key: 'role', title: 'Роль', render: (r) => label(r.role) },
    { key: 'snapshot', title: 'Снимок', render: (r) => shortId(r.snapshot) },
  ];

  const itemPartyCols: Column<ItemParty>[] = [
    { key: 'item', title: 'Позиция', render: (r) => data.items.find((i) => i.id === r.item)?.title ?? shortId(r.item) },
    { key: 'party', title: 'Участник', render: (r) => nameOf(partyNames, r.party) },
    { key: 'role', title: 'Роль', render: (r) => label(r.role) },
  ];

  return (
    <div>
      <Card title="Шапка заявки">
        <ErrorBox error={summary.error} onRetry={summary.reload} />
        <div className="kv-grid">
          <KV k="Клиент" v={header?.client_name ?? '—'} />
          <KV k="Основной контакт" v={header?.primary_contact
            ? `${header.primary_contact.name}${header.primary_contact.position ? ` — ${header.primary_contact.position}` : ''}`
            : '—'} />
          <KV k="Ответственный" v={header?.owner?.full_name ?? '—'} />
          <KV k="Стадия" v={header?.stage_label ?? label(request.stage)} />
          <KV k="Текущая операция" v={header?.current_operation ?? '—'} />
          <KV k="Обещанный срок" v={fmtDate(header?.promised_due_at ?? request.promised_due_at)} />
        </div>
      </Card>

      {summary.data?.schedule_risk && <ScheduleRiskCard risk={summary.data.schedule_risk} />}

      <SituationCard items={summary.data?.current_situation} onAction={goAction} />

      {summary.data?.finance && <FinanceCard buckets={summary.data.finance} />}

      {summary.data?.qualification && <QualificationCard rows={summary.data.qualification} />}

      {/* === WT4 finance (§12): LTV клиента === */}
      <ClientLtvCard accountId={request.client_account} />

      <Card title="Контакты по заявке">
        <ErrorBox error={contacts.error} onRetry={contacts.reload} />
        <DataTable columns={contactCols} rows={contacts.data ?? []} empty="Контакты не привязаны" />
      </Card>

      <Card title="Участники заявки (плательщик / заявитель / изготовитель)">
        <ErrorBox error={reqParties.error} onRetry={reqParties.reload} />
        <DataTable columns={partyCols} rows={reqParties.data ?? []} empty="Участники не добавлены" />
        <form className="inline-form" onSubmit={addParty}>
          <ErrorBox error={err} />
          <div className="form-row">
            <Field label="Участник">
              <select value={partyId} onChange={(e) => setPartyId(e.target.value)} required>
                <option value="">— выберите —</option>
                {(allParties.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
            <Field label="Роль">
              <select value={role} onChange={(e) => setRole(e.target.value)}>
                <option value="payer">Плательщик</option>
                <option value="applicant">Заявитель</option>
                <option value="manufacturer">Изготовитель</option>
              </select>
            </Field>
          </div>
          <div className="form-actions">
            <button className="btn primary" disabled={busy || !partyId}>Добавить участника</button>
          </div>
        </form>
      </Card>

      <Card title="Участники по позициям">
        <ErrorBox error={itemParties.error} />
        <DataTable columns={itemPartyCols} rows={itemParties.data ?? []} empty="Нет участников по позициям" />
      </Card>

      <Card title="Снимки реквизитов">
        <ErrorBox error={snapshots.error} />
        {(snapshots.data ?? []).length === 0 ? (
          <Empty text="Снимки отсутствуют" />
        ) : (
          <DataTable
            columns={[
              { key: 'version', title: 'Версия', render: (r) => String(r.version ?? '—') },
              { key: 'confirmed_at', title: 'Подтверждён', render: (r) => fmtDate(r.confirmed_at) },
              { key: 'created_at', title: 'Создан', render: (r) => fmtDate(r.created_at) },
            ]}
            rows={snapshots.data ?? []}
          />
        )}
      </Card>
    </div>
  );
}
